export type TimezoneMode = "utc" | "scope" | "local"

export type UtcDateTime = { dateUtc: string; timeUtc: string } // YYYY-MM-DD, HH:MM

export function getSelectedTimezone(
  mode: TimezoneMode,
  observatoryTimezone: string | null
): string | null {
  switch (mode) {
    case "utc":
      return "UTC"
    case "scope":
      return observatoryTimezone
    case "local":
      return null
    default:
      return "UTC"
  }
}

export function getDisplayDateTime(
  utc: UtcDateTime,
  tz: string | null
): { date: string; time: string } {
  const utcDate = new Date(`${utc.dateUtc}T${utc.timeUtc}:00Z`)

  if (tz === "UTC") return { date: utc.dateUtc, time: utc.timeUtc }

  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz || undefined,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })

  const parts = formatter.formatToParts(utcDate)
  const year = parts.find(p => p.type === "year")?.value || ""
  const month = parts.find(p => p.type === "month")?.value || ""
  const day = parts.find(p => p.type === "day")?.value || ""
  const hour = parts.find(p => p.type === "hour")?.value || ""
  const minute = parts.find(p => p.type === "minute")?.value || ""

  return { date: `${year}-${month}-${day}`, time: `${hour}:${minute}` }
}

export function inputToUtcDateTime(
  inputDate: string,
  inputTime: string,
  tz: string | null
): UtcDateTime {
  if (tz === "UTC") return { dateUtc: inputDate, timeUtc: inputTime }

  // Local timezone
  if (!tz) {
    const local = new Date(`${inputDate}T${inputTime}:00`)
    const utcYear = local.getUTCFullYear()
    const utcMonth = String(local.getUTCMonth() + 1).padStart(2, "0")
    const utcDay = String(local.getUTCDate()).padStart(2, "0")
    const utcHours = String(local.getUTCHours()).padStart(2, "0")
    const utcMinutes = String(local.getUTCMinutes()).padStart(2, "0")
    return {
      dateUtc: `${utcYear}-${utcMonth}-${utcDay}`,
      timeUtc: `${utcHours}:${utcMinutes}`,
    }
  }

  // Specific IANA timezone: interpret input as being in that timezone.
  const [year, month, day] = inputDate.split("-").map(Number)
  const [hours, minutes] = inputTime.split(":").map(Number)

  // Start with an as-if-UTC guess.
  const asUtc = new Date(Date.UTC(year, month - 1, day, hours, minutes))

  // See what moment that corresponds to in the target timezone.
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })

  const parts = formatter.formatToParts(asUtc)
  const tzYear = parseInt(parts.find(p => p.type === "year")?.value || "0", 10)
  const tzMonth = parseInt(parts.find(p => p.type === "month")?.value || "0", 10)
  const tzDay = parseInt(parts.find(p => p.type === "day")?.value || "0", 10)
  const tzHours = parseInt(parts.find(p => p.type === "hour")?.value || "0", 10)
  const tzMinutes = parseInt(parts.find(p => p.type === "minute")?.value || "0", 10)

  const tzAsLocal = new Date(tzYear, tzMonth - 1, tzDay, tzHours, tzMinutes)
  const inputAsLocal = new Date(year, month - 1, day, hours, minutes)
  const diffMs = inputAsLocal.getTime() - tzAsLocal.getTime()

  const correctUtc = new Date(asUtc.getTime() + diffMs)
  const utcYear = correctUtc.getUTCFullYear()
  const utcMonth = String(correctUtc.getUTCMonth() + 1).padStart(2, "0")
  const utcDay = String(correctUtc.getUTCDate()).padStart(2, "0")
  const utcHours = String(correctUtc.getUTCHours()).padStart(2, "0")
  const utcMinutes = String(correctUtc.getUTCMinutes()).padStart(2, "0")

  return { dateUtc: `${utcYear}-${utcMonth}-${utcDay}`, timeUtc: `${utcHours}:${utcMinutes}` }
}


